import { Card, Col, Row, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

const { Text } = Typography;

export interface SubModuleTile {
  /** Absolute client route (e.g. "/finance/coa"). */
  path: string;
  /** Localized title — usually a t() call from the caller. */
  title: string;
  /** Optional one-line description. */
  description?: string;
  /** Lead icon — typically an AntD outlined icon already coloured. */
  icon: ReactNode;
  /** Quick stat shown next to the title (e.g. "24 accounts", "SAR 18M"). */
  stat?: string;
  /** Optional accent color — defaults to brand primary. */
  accentColor?: string;
}

export interface SubModuleGridProps {
  items: SubModuleTile[];
  /** md-breakpoint columns (default 8 → 3-up). 4-col uses md=6 (4-up). */
  cols?: '3-col' | '4-col';
}

/**
 * Sub-module tile grid — used by module landing pages (`/finance`,
 * `/engineering`, etc.) to surface every page inside the module as a
 * clickable card with a quick stat.
 *
 * Mirrors the Landing role-tile pattern but with stats instead of role
 * descriptions, and arrow + accent border instead of the role-coloured
 * icon chip.
 */
export default function SubModuleGrid({ items, cols = '3-col' }: SubModuleGridProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.language === 'ar';
  const md = cols === '4-col' ? 6 : 8;

  return (
    <Row gutter={[16, 16]}>
      {items.map((item) => {
        const accent = item.accentColor ?? 'rgb(31, 77, 58)';
        return (
          <Col xs={24} sm={12} md={md} key={item.path}>
            <Card
              hoverable
              onClick={() => navigate(item.path)}
              styles={{
                body: {
                  padding: 16,
                  borderInlineStart: `3px solid ${accent}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                },
              }}
              style={{ height: '100%', cursor: 'pointer' }}
            >
              <span
                aria-hidden
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: `${accent}15`,
                  color: accent,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'rgba(26, 31, 27, 0.92)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.title}
                </div>
                {item.stat && (
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: 'block', marginTop: 2 }}
                  >
                    {item.stat}
                  </Text>
                )}
                {item.description && (
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 11,
                      display: 'block',
                      marginTop: 4,
                      lineHeight: 1.4,
                    }}
                  >
                    {item.description}
                  </Text>
                )}
              </div>
              <ArrowRightOutlined
                style={{
                  color: accent,
                  fontSize: 14,
                  flexShrink: 0,
                  transform: isRtl ? 'rotate(180deg)' : 'none',
                }}
              />
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}
